import { useEffect, useMemo, useState } from "react";
import { encodeQR } from "@/shared/utils/qr/encodeQR";
import { snapToWholeModules } from "@/shared/utils/qr/qrLayout";

// Light modules the scanner needs around the symbol, in module units.
const QUIET_ZONE = 4;

const DEFAULT_SIZE_PX = 208;

function readDevicePixelRatio(): number {
  if (typeof window === "undefined") return 1;
  return window.devicePixelRatio || 1;
}

/**
 * The ratio as it is now, re-read when it changes — which happens when the
 * window moves to a second monitor, and when the user zooms the page.
 */
function useDevicePixelRatio(): number {
  const [ratio, setRatio] = useState(readDevicePixelRatio);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    // The query only fires while the ratio is the one it was built from, so it
    // is rebuilt on every change — there is no "any resolution change" event.
    const query = window.matchMedia(`(resolution: ${ratio}dppx)`);
    const onChange = () => setRatio(readDevicePixelRatio());

    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [ratio]);

  return ratio;
}

type Props = {
  value: string;
  // Requested edge length in CSS pixels. The symbol is drawn at the nearest
  // size that gives every module a whole number of device pixels, so the
  // rendered element can end up a little smaller or larger than this.
  size?: number;
  className?: string;
  title?: string;
};

/**
 * Renders `value` as a scannable QR code. The symbol is one SVG path so it stays
 * crisp at any size and needs no canvas, image host, or runtime dependency.
 *
 * Two details decide whether a phone can read it off the screen:
 *
 * - The edge length is snapped so that one module is a whole number of device
 *   pixels (`snapToWholeModules`), because otherwise the rasteriser rounds each
 *   module on its own and the grid stops being regular. That is why the same
 *   code can be unreadable on one screen and fine on another.
 * - Dark modules are merged into horizontal runs, one path segment per run
 *   rather than per module, so neighbours in a row share no seam to antialias.
 *
 * Deliberately no `shape-rendering="crispEdges"`: it reintroduces exactly the
 * per-module rounding above whenever the snapping cannot be exact — under
 * browser zoom, or when `max-width` shrinks the symbol inside a narrow embed.
 * Uniform antialiasing degrades far better than an irregular grid.
 */
export function QRCode(props: Props) {
  const symbol = useMemo(() => {
    try {
      const { size, modules } = encodeQR(props.value);
      const segments: string[] = [];

      for (let row = 0; row < size; row += 1) {
        let runStart = -1;

        for (let column = 0; column <= size; column += 1) {
          const isDark = column < size && modules[row][column];

          if (isDark && runStart === -1) runStart = column;
          if (isDark || runStart === -1) continue;

          const length = column - runStart;
          segments.push(
            `M${runStart + QUIET_ZONE} ${row + QUIET_ZONE}h${length}v1h-${length}z`,
          );
          runStart = -1;
        }
      }

      return { d: segments.join(""), extent: size + QUIET_ZONE * 2 };
    } catch (error) {
      console.error("[configurator] Failed to render QR code:", error);
      return null;
    }
  }, [props.value]);

  const ratio = useDevicePixelRatio();
  if (!symbol) return null;

  const edge = snapToWholeModules(
    props.size ?? DEFAULT_SIZE_PX,
    symbol.extent,
    ratio,
  );

  return (
    <svg
      className={["qr-code", props.className].filter(Boolean).join(" ")}
      width={edge}
      height={edge}
      viewBox={`0 0 ${symbol.extent} ${symbol.extent}`}
      role="img"
      aria-label={props.title ?? props.value}
    >
      <rect
        className="qr-code__background"
        width={symbol.extent}
        height={symbol.extent}
        fill="#ffffff"
      />
      <path className="qr-code__modules" d={symbol.d} fill="#111111" />
    </svg>
  );
}
